import type { MappedAnswers } from '@/redux/slider';
import { Prio, Variation, SuccessBoxItem, SuccessBox, PrioGroup } from '@/components/landing-page/landing-page.types';
import type { SuccessBoxProps } from '@/components/landing-page/components/success-box';
import type { GroupType } from '@/components/landing-page/components/group';
import Replacer from '@/components/landing-page/utils/replacer';
import Resolver from '@/components/landing-page/utils/resolver';
import PRIO_GROUP_TYPE from '@/components/landing-page/utils/prio-group-type';

export default class Parser {
  private readonly prio: Prio;
  private readonly replacer: Replacer;
  private readonly resolver: Resolver;
  private readonly variation: Variation;
  public readonly page: {
    successBox: SuccessBoxProps;
    question: GroupType[];
    sidebar: GroupType[];
    footer: GroupType[];
  };

  constructor(prio: Prio, mappedAnswers: MappedAnswers, width: number) {
    this.prio = prio;
    this.replacer = new Replacer(mappedAnswers);
    this.resolver = new Resolver(mappedAnswers, width);
    this.variation = this.getVariation(prio);
    this.page = this.deepMapStrings({
      successBox: this.getSuccessBoxProps(),
      question: this.getLandingPageGroups(prio.question),
      sidebar: this.getLandingPageGroups(prio.sidebar),
      footer: this.getLandingPageGroups(prio.footer),
    });
  }

  /**
   * Go through all nested levels of record's keys and look to every value of type string or array that contains string.
   * Pass this string to Replacer and replace value with function output, values that are class constructor calls are
   * passed as they are, without any change, e.x. new Date(), new Map(), new Set()
   */
  private deepMapStrings<T extends Record<string, unknown>>(record: T): T {
    const traverse = (value: unknown): unknown => {
      if (typeof value === 'string') {
        return this.replacer.replace(value);
      } else if (Array.isArray(value)) {
        return value.map(traverse);
      } else if (value instanceof Date) {
        return value;
      } else if (value && typeof value === 'object' && value.constructor === Object) {
        return Object.entries(value).reduce(
          (acc, [key, val]) => {
            acc[key] = traverse(val);
            return acc;
          },
          {} as Record<string, unknown>,
        );
      }
      return value;
    };
    return traverse(record) as T;
  }

  /**
   * Returns the first item in the list that satisfies the condition, or the first item without a condition if none do.
   * Returns null if no suitable item is found.
   */
  private getLastResolvedItem(list: Variation[] | null): Variation | null {
    if (!list) return null;
    const resolved = list.filter((item) => item.condition && this.resolver.check(item.condition))[0];
    return resolved ?? list.filter((item): boolean => !item.condition)[0] ?? null;
  }

  /**
   * Determines the appropriate variation based on the provided prio. Attempts to resolve the first matching variation
   * with a satisfied condition. Falls back to the default variation schema if none match or parsing fails.
   */
  private getVariation(prio: Prio): Variation {
    const fallbackVariation: Variation = {
      color: '#00b649',
      order: ['title', 'list', 'html'],
      head: 1,
      title: 1,
      html: 1,
      list: 1,
      condition: null,
    };
    return this.getLastResolvedItem(prio.variation) || fallbackVariation;
  }

  /**
   * Retrieves a specific item from a list based on variationId and optional condition.
   * Searches through the provided list and returns the first item where:
   * - variationId matches the provided variation, and
   * - if item has condition, it must pass the resolver.check() test.
   */
  private getSuccessBoxItem<L extends SuccessBoxItem>(list: Array<L>, variation: number | null): L | undefined {
    return list.find(({ variationId, condition }) => {
      const variationIdMatched = variationId === variation;
      return condition ? variationIdMatched && this.resolver.check(condition) : variationIdMatched;
    });
  }

  /**
   * Get ready to use blueprint for Sidebar with all elements are checked for condition selected according to variation.
   * List items are sorted according to priority that is provided.
   */
  private getSuccessBoxProps(): SuccessBoxProps {
    const defaultSuccessBox: SuccessBox = {
      head: [],
      body: {
        title: [],
        html: [],
        list: [],
      },
    };
    const successBox = this.prio.successBox || defaultSuccessBox;
    const head = this.getSuccessBoxItem(successBox.head, this.variation.head);
    const title = this.getSuccessBoxItem(successBox.body.title, this.variation.title);
    const html = this.getSuccessBoxItem(successBox.body.html, this.variation.html);
    const list = this.getSuccessBoxItem(successBox.body.list, this.variation.list);
    const { content, priority } = list ? list.content : { priority: [], content: [] };
    return {
      color: this.variation.color,
      head: {
        primary: head ? head.content.primary : '',
        secondary: head ? head.content.secondary : '',
      },
      body: this.variation.order.map((type) => {
        switch (type) {
          case 'list':
            return {
              type,
              data: content
                .filter(({ condition }) => !condition || this.resolver.check(condition))
                .sort((a, b) => {
                  const aIndex = priority.indexOf(a.type);
                  const bIndex = priority.indexOf(b.type);
                  const safeAIndex = aIndex === -1 ? priority.length : aIndex;
                  const safeBIndex = bIndex === -1 ? priority.length : bIndex;
                  return safeAIndex - safeBIndex;
                }),
            };
          case 'title':
            return {
              type,
              data: title ? title.content : '',
            };
          case 'html':
            return {
              type,
              data: html ? html.content : '',
            };
        }
      }),
    };
  }

  /**
   * Get ready to use blueprint for Sidebar, Footer or Question with all elements are checked for condition.
   */
  private getLandingPageGroups(data: PrioGroup[] | null): GroupType[] {
    const groups = Array.isArray(data) ? data.map((d) => d).filter((d) => !!d) : [];
    return groups
      .filter((group) => !group.condition || this.resolver.check(group.condition))
      .map((group) => ({ type: group.type, content: group.content }))
      .map((group, i, groups) => {
        const groupHasLine = (group: Omit<PrioGroup, 'condition'>): boolean =>
          groups.length >= 2 &&
          [PRIO_GROUP_TYPE.HOW_TO_GO_NEXT, PRIO_GROUP_TYPE.ADVANTAGE_LIST, PRIO_GROUP_TYPE.REVIEW].some(
            (g) => g === group.type,
          );
        const prevGroup = groups[i - 1] || null;
        const previous = {
          top: prevGroup ? groupHasLine(prevGroup) : false,
          bottom: prevGroup ? groupHasLine(prevGroup) : false,
        };
        return {
          id: Math.ceil(Math.random() * 1000000),
          type: group.type,
          content: group.content,
          showTopLine: groupHasLine(group) && !previous.bottom && i > 1,
          showBottomLine: groupHasLine(group) && i < groups.length - 1,
        };
      });
  }
}
